/**
 * אבטחה וביקורת — the security home of the control plane.
 *
 * ── THE ONE FACT THIS SCREEN EXISTS TO STATE ────────────────────────────────────────────────────
 *
 * How many people at GL-POINT can currently reach a customer's data, and through which grant. A
 * support grant is the ONLY bridge from the platform surface into tenant content (04 §13.1.5, §6.4),
 * so the count of LIVE grants is the count of ways in — and zero is the answer an Owner should be
 * able to confirm at a glance rather than infer from an empty table further down.
 *
 * The grant queue itself already has its own screen, because a grant is requested by one principal
 * and approved by a different one and that queue is somebody's working list. This is the summary
 * above it, plus the two other security surfaces the Owner has.
 *
 * ── AND THE TRAIL IT NOW CARRIES ────────────────────────────────────────────────────────────────
 *
 * The first version of this screen stated, in words, that no platform-wide audit read existed. That
 * was true of the ROUTES and false of the CATALOGUE: `platform.audit.billing.read` has been seeded
 * by migration 0005 all along, held by the Owner and the Auditor, and used by no route — a domain
 * that was declared and unreachable rather than absent.
 *
 * So the trail is read here, under exactly that capability and no wider. 04 §8.2 gives an office
 * its OWN trail because that is what makes SupportAccess acceptable: GL-POINT may cross into a
 * customer's data, and the customer sees every crossing. The obligation runs both ways, and this is
 * the other half — a platform operator whose crossings nobody at GL-POINT can review is the same
 * gap seen from the inside.
 *
 * What is still NOT here is a general cross-tenant audit capability, because the catalogue has
 * none and helping ourselves to a neighbouring one would be granting in code what was never
 * granted in the catalogue. That gap is written out below rather than quietly covered.
 */

import { useCallback } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ServerRead } from '../components/ServerRead';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { auditOutcomeHe, formatInstantHe } from '../api/adminVocabulary';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { PlatformAuditEventSummary, SupportGrantSummary } from '../api/contract';
import { DataTable } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { ServerRead as ServerReadBlock } from '../components/ServerRead';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { routes } from '../routes';

const TITLE_HE = 'אבטחה וביקורת';

/**
 * WHAT AN AUDIT ROW SHOWS, AND WHAT IT DOES NOT.
 *
 * An action, an entity TYPE, an outcome and the customer it touched. Never an entity's contents:
 * `audit.audit_event` carries no business row and this table has no column one could arrive in.
 * The support grant is shown whenever there is one, because a crossing into a customer's data
 * under a grant is the single fact this trail exists to make visible.
 */
const AUDIT_COLUMNS: readonly Column<PlatformAuditEventSummary>[] = [
  {
    key: 'when',
    headerHe: 'מתי',
    render: (e) => <span dir="ltr">{formatInstantHe(e.occurredAt) ?? e.occurredAt}</span>,
  },
  {
    key: 'tenant',
    headerHe: 'לקוח',
    // NULL is a real answer: a platform-context event belongs to no customer, and printing a dash
    // for it would read as missing data rather than as "this was our own staff, not a client".
    render: (e) => (e.tenantUid === null ? <span>הפלטפורמה עצמה</span> : <span dir="ltr">{e.tenantUid}</span>),
  },
  { key: 'action', headerHe: 'פעולה', render: (e) => <span dir="ltr">{e.action}</span> },
  { key: 'entity', headerHe: 'ישות', render: (e) => <span dir="ltr">{e.entityType}</span> },
  {
    key: 'outcome',
    headerHe: 'תוצאה',
    render: (e) => {
      const term = auditOutcomeHe(e.outcome === 'allow' ? 'allow' : 'deny');
      return <StatusChip labelHe={term.labelHe} tone={term.tone} />;
    },
  },
  /*
   * WHY IT WAS REFUSED. **Owner instruction, 2026-10-23.**
   *
   * `denyReason` has always been on the wire — it is a field of `PlatformAuditEventSummary` and
   * `platform.audit.read` returns it — and this table dropped it. So every refusal rendered as an
   * identical red chip, and the one fact a refusal exists to carry was in the browser's memory and
   * on no screen.
   *
   * It cost a real diagnosis. An invitation redemption refused on this estate writes
   * `action = auth.invitation.redeemed` with the reason in this column — `expired`, `revoked`,
   * `already_accepted`, `wrong_portal`, `tenant_not_operating`, `not_found` — and reading it
   * required going to the database, because the screen that already had the answer did not show it.
   *
   * NO ORACLE ARGUMENT APPLIES HERE. The anonymous redemption endpoint answers every refusal with
   * one uniform shape, deliberately, so that a stranger cannot learn which tokens exist. This table
   * is the platform owner reading his own trail, where the action and the customer are already
   * shown: withholding the reason protects nobody and blinds the only person who can act.
   *
   * LTR, and the code verbatim. These are closed-vocabulary identifiers that appear in the audit
   * table and in `RedemptionRefusal`; translating them would make the screen and the record
   * disagree about the name of the same fact.
   */
  {
    key: 'reason',
    headerHe: 'סיבת הסירוב',
    render: (e) =>
      e.denyReason === null ? null : (
        <span dir="ltr" className="audit-reason">
          {e.denyReason}
        </span>
      ),
  },
  {
    key: 'grant',
    headerHe: 'הרשאת תמיכה',
    render: (e) => (e.supportGrantUid === null ? null : <span dir="ltr">{e.supportGrantUid}</span>),
  },
];

/** A grant with nothing in `blockedBy` is live: somebody can reach a customer's data right now. */
const isLive = (g: SupportGrantSummary): boolean => g.blockedBy.length === 0;

export function PlatformSecurityScreen(): JSX.Element {
  const ctx = useWorkContext();

  const read = useCallback(() => callOperation('platform.support_grant.read', {}), []);
  const grantsRead = useServerData(read);

  /*
   * THE PLATFORM'S OWN TRAIL. This screen used to say, in words, that no such read existed -- which
   * was true and is no longer. `platform.audit.read` is gated on `platform.audit.billing.read`, the
   * capability the accepted catalogue already seeded for exactly this and which no route had ever
   * used, so the domain was DECLARED and unreachable rather than absent.
   */
  const readAudit = useCallback(() => callOperation('platform.audit.read', {}), []);
  const auditRead = useServerData(readAudit);

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'קונסולת הפלטפורמה', href: routes.platformConsole }}
    >
      <ServerRead result={grantsRead.result} whatHe="את מצב הרשאות התמיכה" skeletonRows={3}>
        {(data) => {
          const live = data.grants.filter(isLive).length;
          const waiting = data.grants.filter((g) => !isLive(g)).length;
          const tenants = new Set(data.grants.filter(isLive).map((g) => g.tenantUid)).size;

          return (
            <>
              <section className="figures" aria-label="הרשאות תמיכה">
                <article className="figure" data-zero={live === 0 ? 'true' : 'false'}>
                  <p className="figure__label">הרשאות פעילות כרגע</p>
                  <p className="figure__value" dir="ltr">
                    {live}
                  </p>
                  <p className="figure__note">כניסות פתוחות לנתוני לקוח ברגע זה</p>
                </article>
                <article className="figure">
                  <p className="figure__label">ארגונים שנגישים כרגע</p>
                  <p className="figure__value" dir="ltr">
                    {tenants}
                  </p>
                </article>
                <article className="figure">
                  <p className="figure__label">ממתינות לאישור או להסכמה</p>
                  <p className="figure__value" dir="ltr">
                    {waiting}
                  </p>
                </article>
              </section>

              {live === 0 ? (
                /*
                 * THE STATEMENT, NOT THE ABSENCE. The read succeeded and returned no live grant, so
                 * the screen says the fact -- nobody at GL-POINT can reach a customer's data right
                 * now -- rather than showing a blank area that a reader has to interpret.
                 */
                <StateBlock
                  titleHe="אף אחד אינו נמצא כרגע בנתוני לקוח"
                  bodyHe="הקריאה לשרת הצליחה והחזירה שאין הרשאת תמיכה פעילה. הרשאת תמיכה היא הדרך היחידה שבה הפלטפורמה מגיעה לנתוני ארגון."
                  tone="empty"
                  nextStep={
                    <a className="btn" href={routes.platformSupportGrants}>
                      פתיחת מסך הרשאות התמיכה
                    </a>
                  }
                />
              ) : (
                <div className="actions-row">
                  <a className="btn btn--nav" href={routes.platformSupportGrants}>
                    פתיחת מסך הרשאות התמיכה
                  </a>
                </div>
              )}
            </>
          );
        }}
      </ServerRead>

      {/*
        NOT BUILT, AND SAID SO. Outside the read, because this is a property of the product rather
        than of the answer -- it reads identically whether the estate is busy or empty.
      */}
      {/*
        THE AUDIT TRAIL, READ FROM THE SERVER.
        This block used to be a written statement that no such read existed. It did not exist; the
        CAPABILITY did — `platform.audit.billing.read`, seeded by migration 0005 and used by no
        route — so the domain was declared and unreachable rather than absent, which is the exact
        pattern this programme keeps finding.
      */}
      <section aria-label="יומן ביקורת">
        <h3 className="breakdown__title">יומן הביקורת של הפלטפורמה</h3>
        <ServerReadBlock result={auditRead.result} whatHe="את יומן הביקורת" skeletonRows={6}>
          {(data) => (
            <DataTable<PlatformAuditEventSummary>
              captionHe="אירועי ביקורת"
              columns={AUDIT_COLUMNS}
              rows={data.events}
              rowKey={(e) => `${e.occurredAt}:${e.action}:${e.routeId ?? ''}`}
              filterSummaryHe={`מציג ${data.events.length} אירועים אחרונים`}
              emptyStateHe="הקריאה הצליחה ולא נרשמו אירועים. זו תשובת השרת, לא תקלה."
            />
          )}
        </ServerReadBlock>
      </section>

      <section className="not-available" aria-label="מה אינו זמין כאן">
        <h3 className="breakdown__title">מה אינו מוצג כאן, ולמה</h3>
        <dl className="not-available__list">
          <div className="not-available__item">
            <dt>
              התראות אבטחה בזמן אמת —{' '}
              <span className="not-available__flag">טרם הוגדר יעד להתראות</span>
            </dt>
            <dd>
              היומן שלמעלה מציג את מה שכבר קרה. התראה מיידית דורשת יעד — כתובת דוא"ל, טלפון או
              מערכת ניטור — שטרם נקבע, ולכן אין כאן רשימת התראות. עד שייקבע יעד, בדיקת היומן היא
              הדרך לראות אירוע חריג.
            </dd>
          </div>
        </dl>
      </section>

      <section className="tiles">
        <article className="tile" data-available="true">
          <h2 className="tile__title">האבטחה שלי</h2>
          <p className="tile__lead">
            התחברויות ואימות דו-שלבי. חובה לחשבון פלטפורמה ואינה ניתנת לביטול.
          </p>
          <a className="btn btn--nav" href={routes.personalSecurity}>
            פתיחה
          </a>
        </article>
      </section>
    </AppShell>
  );
}
